import { useMemo, useState } from 'react';
import classnames from 'classnames';
import { Tooltip } from '@nike/eds';
import * as R from 'ramda';
import { useDebouncedCallback } from 'use-debounce';

type ProductWallItemProps = {
  data: WallItem;
};

const api_url = process.env.REACT_APP_NIKE_API;

const getProductUrl = (product: WallItem) => {
  const dotComHost = 'https://www.nike.com.cn';
  const isDotCom = product.channelId === 'd9a5bc42-4b9c-4976-858a-f159cf99c647';
  const slug = R.path(['productInfo', 0, 'productContent', 'slug'], product);
  const pbid = R.path(
    ['productInfo', 0, 'customizedPreBuild', 'legacy', 'pbid'],
    product,
  );
  const styleColor = R.path(
    ['productInfo', 0, 'merchProduct', 'styleColor'],
    product,
  );
  const dotComUrl = `${dotComHost}/t/${slug}/${styleColor}`;
  const dotComNBYUrl = `${dotComHost}/u/${slug}/${pbid}`;
  const fallbackUrl = `${api_url}/product_feed/threads/v2/${product.id}?channelId=${product.channelId}&marketplace=${product.marketplace}&language=${product.language}`;
  const productUrl = isDotCom
    ? pbid === undefined
      ? dotComUrl
      : dotComNBYUrl
    : fallbackUrl;
  return productUrl;
};
const mapProductItemHelper = (product: WallItem) => {
  const colors =
    R.path(['productInfo', 0, 'productContent', 'colors'], product) || [];
  const colorNames = colors.map((c) => c.name).join('/');
  const colorTypes = colors.map((c) => c.type).join('/');
  const uniqueColors = Array.from(new Set(colors.map((c) => c.hex)));
  const item = {
    colorNames,
    colorTypes,
    uniqueColors,
    title: R.path(['productInfo', 0, 'productContent', 'title'], product),
    subtitle: R.path(['productInfo', 0, 'productContent', 'subtitle'], product),
    colorDescription: R.path(
      ['productInfo', 0, 'productContent', 'colorDescription'],
      product,
    ),
    styleColor: R.path(
      ['productInfo', 0, 'merchProduct', 'styleColor'],
      product,
    ),
    pbid: R.path(
      ['productInfo', 0, 'customizedPreBuild', 'legacy', 'pbid'],
      product,
    ),
    imageUrl:
      R.path(
        [
          'publishedContent',
          'properties',
          'productCard',
          'properties',
          'squarishURL',
        ],
        product,
      ) || R.path(['productInfo', 0, 'imageUrls', 'productImageUrl'], product),
    url: getProductUrl(product),
    available: R.path(['productInfo', 0, 'availability', 'available'], product),
    currency: R.path(['productInfo', 0, 'merchPrice', 'currency'], product),
    currentPrice: R.path(
      ['productInfo', 0, 'merchPrice', 'currentPrice'],
      product,
    ),
    fullPrice: R.path(['productInfo', 0, 'merchPrice', 'fullPrice'], product),
  };
  return item;
};

export default function ProductWallItem(props: ProductWallItemProps) {
  const [visibleProductIdx, setVisibleProductIdx] = useState(0);
  const { data: product } = props;
  const strategy = product.explain.ranking;

  const rollupProducts = useMemo(() => {
    const thisProduct = [product].map(mapProductItemHelper);
    if (product.rollup.totalThreads > 1) {
      const rollups = product.rollup.threads.map(mapProductItemHelper);
      return thisProduct.concat(rollups);
    } else {
      return thisProduct;
    }
  }, [product]);

  const handleMouseEnter = useDebouncedCallback((idx: number) => {
    if (rollupProducts.length < 2 || visibleProductIdx === idx) return;
    setVisibleProductIdx(idx);
  }, 100);

  return (
    <div
      className={classnames(
        'tw-rounded tw-bg-white tw-w-60 tw-h-800 tw-relative tw-rounded-br-{1.25rem}',
        {
          'tw-opacity-50': !rollupProducts[visibleProductIdx].available,
        },
      )}
      style={{ boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.1)' }}
    >
      <div className='tw-flex tw-justify-between tw-items-start tw-px-1'>
        <div className='tw-pt-1 tw-text-[10px] tw-font-bold'>
          <div
            className='tw-rounded-full tw-bg-gray-300 tw-text-center tw-bg-primary'
            style={{
              padding: '2px 4px',
              fontSize: '.7rem',
              lineHeight: 1,
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            {strategy.position + 1}
          </div>
        </div>
        <div className=' tw-text-right tw-flex tw-flex-col'>
          {true && (
            <Tooltip
              bodySlot={
                <div className='tw-text-gray-300 tw-text-[9px] tw-w-20 tw-text-center'>
                  {/* {strategy.strategy.detail
                  ? strategy.strategy.detail.strategyId
                  : 'no strategyId'} */}
                  {strategy?.strategy?.detail?.strategyId ?? 'no strategyId'}
                </div>
              }
              isDark
              placement={'top'}
            >
              <div className='tw-text-[10px] tw-font-bold'>
                {strategy.strategy.name}
              </div>
            </Tooltip>
          )}
          <div className='tw-text-[7px] tw-font-bold tw-text-zinc-600 tw-uppercase'>
            {product.explain.ranking.recall?.recallPath.join(', ') || ''}
          </div>
        </div>
      </div>
      <div className='tw-px-5 tw-pb-3'>
        {rollupProducts.map((pdt, idx) => {
          const key = `${pdt.styleColor || pdt.pbid}`;
          return (
            <div
              key={key}
              className={classnames({ 'tw-hidden': visibleProductIdx !== idx })}
            >
              <a href={pdt.url} target='_blank' rel='noreferrer'>
                <img
                  src={pdt.imageUrl as string}
                  className='rounded tw-object-fit'
                  alt='product'
                  style={{ width: 200, height: 200 }}
                />
              </a>
              {rollupProducts.length > 1 && (
                <div className='tw-mt-2'>
                  <div className='tw-flex tw-gap-1 tw-justify-start tw-flex-wrap'>
                    {rollupProducts.map((p, idx) => {
                      return (
                        <a
                          key={p.styleColor}
                          href={p.url}
                          target='_blank'
                          rel='noreferrer'
                          className='tw-block'
                          onMouseEnter={() => handleMouseEnter(idx)}
                        >
                          <div
                            className={classnames(
                              'tw-h-8 tw-w-8 tw-bg-center tw-bg-contain tw-bg-no-repeat tw-bg-slate-200',
                              {
                                'tw-border-2 tw-border-indigo-500/100':
                                  idx === visibleProductIdx,
                              },
                            )}
                            style={{ backgroundImage: `url(${p.imageUrl})` }}
                          ></div>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className=''>
                <a
                  className='tw-font-bold'
                  href={pdt.url}
                  target='_blank'
                  rel='noreferrer'
                >
                  {pdt.title}
                </a>
                <p className='tw-font-bold tw-text-gray-500'>{pdt.subtitle}</p>
                <p className='tw-text-sm'>{pdt.styleColor}</p>
                {typeof pdt.pbid === 'string' && (
                  <p>
                    <Tooltip isDark placement='top' bodySlot='PBID'>
                      <p className='tw-text-sm'>{pdt.pbid}</p>
                    </Tooltip>
                  </p>
                )}
                <Tooltip
                  bodySlot={
                    <div className='tw-text-gray-100 tw-text-xs tw-font-bold'>
                      {pdt.colorTypes}
                    </div>
                  }
                  isDark
                  placement='top'
                >
                  <div>
                    <p className='tw-text-sm'>{pdt.colorNames}</p>
                    <div className='tw-flex tw-items-center tw-pl-1'>
                      {pdt.uniqueColors.map((color, index) => (
                        <div
                          key={index}
                          className='tw-w-4 tw-h-4 tw-rounded-full tw-mr-2'
                          style={{
                            backgroundColor: '#' + color,
                            boxShadow: '0px 0px 8px 0px rgba(0,0,0,0.5)',
                          }}
                        ></div>
                      ))}
                    </div>
                  </div>
                </Tooltip>
                <p className='tw-text-sm'>
                  {pdt.currency}:{' '}
                  {pdt.fullPrice !== pdt.currentPrice ? (
                    <>
                      <del className='tw-text-gray-500'>{pdt.fullPrice}</del>{' '}
                      <span className='tw-text-black-500 tw-font-bold'>
                        {pdt.currentPrice}
                      </span>
                    </>
                  ) : (
                    <span className='tw-text-black-500 tw-font-bold'>
                      {pdt.currentPrice}
                    </span>
                  )}
                </p>
                {!pdt.available && (
                  <p className='tw-text-sm tw-font-bold tw-text-red-500'>
                    Out of Stock
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
